import { lighten } from "@mui/system";
import styled from "styled-components";

const svgPrefix =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="%23ff373c" color="%23ff373c">';
const svgSuffix = "</path></svg>";

export const ArticleBodyStyles = styled.div(({ theme }) => ({
  "& a": {
    color: "inherit",

    "&:hover": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  },

  // basic styling for hr
  "& hr": {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderWidth: "1px 0 0 0",
    margin: "32px 0",
  },

  // ensure images don't overflow
  "& img": {
    maxWidth: "100%",
  },

  // basic heading styling
  "& h1, & h2, & h3, & h4": {
    fontWeight: 600,
    color: theme.palette.getColorVariantCSSColor("neutralDark"),
    // overwrite for any heading with <b> tags
    "& b": {
      fontWeight: 600,
    },
  },

  "& h1": {
    fontSize: 24,
    lineHeight: "28px",
    marginTop: 40,
    marginBottom: 0,
  },
  "& h2": {
    fontSize: 18,
    lineHeight: "24px",
    marginTop: 48,
    marginBottom: 0,
  },
  "& h3": {
    fontSize: 16,
    lineHeight: "24px",
    marginTop: 40,
    marginBottom: 0,
  },
  "& h4": {
    fontSize: 14,
    lineHeight: "24px",
    marginTop: 32,
    marginBottom: 0,
  },
  // headings followed by other headings should be closer to one another
  // as they are logically related
  "& h2 + h3": { marginTop: 24 },
  "& h3 + h4": { marginTop: 16 },

  "& p": {
    fontSize: 14,
    lineHeight: "24px",
    marginTop: 16,
    marginBottom: 0,
  },

  "& .intercom-interblocks-callout": {
    marginTop: 32,
    padding: "24px 16px",
    [`${theme.breakpoints.up("sm")}`]: {
      padding: "32px",
    },

    backgroundColor: `${lighten(theme.palette.info.main, 0.92)} !important`,
    borderRadius: `${theme.shape.borderRadii.large}px`,

    "& > *:first-child": {
      marginTop: 0,
    },
  },

  // Intercom Video Sizing
  "& .intercom-h2b-video": {
    "& iframe": {
      width: "100%",
      height: "100%",
      aspectRatio: "16/9",
    },
  },

  // --------------------------------
  // ACCORDION
  // --------------------------------

  // The first accordion in any set of accordions gets some
  // top margin and a top border (as well as a border like all
  // other accordions do)
  "& details:not(details + details)": {
    marginTop: "24px",
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },

  "& details": {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,

    "& summary": {
      cursor: "pointer",
      padding: "12px 0",

      "& > h2, & > h3": { fontSize: 16 },
      "& > h2, & > h3, & > h4": {
        position: "relative",
        fontWeight: 600,
        margin: "0 !important",

        // open (+) icon
        "&::after": {
          position: "absolute",
          content: '""',
          top: "-12px",
          right: "-12px",
          borderRadius: "50%",
          height: 48,
          width: 48,
          transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

          background: `no-repeat center center url('${svgPrefix}<path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z">${svgSuffix}')`,
          // FUTURE: chevron styling
          //background: `no-repeat center center url('${svgPrefix}<path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z">${svgSuffix}')`,
        },
      },

      // hide default arrow
      "&::marker": {
        content: "none",
      },
      "&::-webkit-details-marker": {
        display: "none",
      },

      "&:hover > h2::after, &:hover > h3::after, &:hover > h4::after": {
        backgroundColor: theme.palette.grey[100],
      },
    },
    // close (-) icon
    "&[open] > summary > h2:after, &[open] > summary > h3:after, &[open] > summary > h4:after": {
      transform: "rotate(180deg)",
      // FUTURE, remove when we go to chevron styling
      backgroundImage: `url('${svgPrefix}<path d="M19 13H5v-2h14v2z">${svgSuffix}')`,
    },
    "& > div": {
      paddingBottom: 16,
      "& ul": {
        marginBottom: 0,
      },
    },
  },

  // existing CSS
  "& table": {
    borderCollapse: "collapse",
    border: `1px solid ${theme.palette.grey[200]}`,
  },

  "& td": {
    verticalAlign: "top",
    textAlign: "left",
    padding: 5,
    p: {
      marginBlockStart: 0,
      marginBlockEnd: 0,
    },
  },
  "& blockquote": {
    padding: 10,
    backgroundColor: theme.palette.grey[100],
    borderLeft: 10,
    borderLeftStyle: "solid",
    borderLeftColor: theme.palette.grey[200],
    marginBlockStart: 0,
    marginBlockEnd: 0,
    marginInlineStart: 0,
    marginInlineEnd: 0,

    "& h3": {
      marginTop: 0,
      marginBottom: 4,
    },

    "& p": {
      marginTop: 4,
      marginBottom: 4,
    },
  },
}));
